import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader } from '@lms/ui';
import { rebaseH5pPayload } from '@/lib/h5p-origin';
import type { H5pPlayerPayload } from './types';
import { useH5pAssets } from './useH5pAssets';
import { useH5pCompletion } from './useH5pCompletion';

interface H5pPlayerProps {
  player: H5pPlayerPayload;
  sectionItemId?: string;
  courseId?: string;
  trackCompletion?: boolean;
}

function cleanupPlayer(root: HTMLElement | null, contentId?: string) {
  if (!root) return;

  root.querySelectorAll('.h5p-initialized').forEach((el) => {
    el.classList.remove('h5p-initialized');
  });

  if (window.H5P?.instances && contentId) {
    window.H5P.instances = window.H5P.instances.filter(
      (instance) => String(instance.contentId) !== String(contentId),
    );
  }
}

export function H5pPlayer({
  player: playerProp,
  sectionItemId,
  courseId,
  trackCompletion = false,
}: H5pPlayerProps) {
  const player = useMemo(
    () => rebaseH5pPayload(playerProp, window.location.origin),
    [playerProp],
  );
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  const { ready, error: assetsError } = useH5pAssets(player.styles ?? [], player.scripts ?? [], {
    integration: player.integration,
  });

  useH5pCompletion(
    sectionItemId,
    trackCompletion && ready && player.available,
    courseId,
    player.contentId,
  );

  useEffect(() => {
    if (!ready || !player.available || !player.integration || !wrapperRef.current) return;

    window.H5PIntegration = player.integration;
    setRuntimeError(null);

    if (window.H5P?.init) {
      try {
        window.H5P.init(wrapperRef.current);
        requestAnimationFrame(() => {
          for (const instance of window.H5P?.instances ?? []) {
            window.H5P?.trigger?.(instance, 'resize');
          }
        });
      } catch {
        setRuntimeError('No se pudo iniciar la actividad H5P.');
      }
    }

    return () => {
      cleanupPlayer(wrapperRef.current, player.contentId);
    };
  }, [ready, player.available, player.integration, player.contentId]);

  if (!player.available) {
    return (
      <div className="lms-h5p-player__empty rounded-lg border border-dash-outline-variant bg-white p-8 text-center text-sm text-dash-on-surface-variant">
        <p>{player.error ?? 'Sin versión publicada para previsualizar.'}</p>
      </div>
    );
  }

  if (assetsError || runtimeError) {
    return (
      <div className="lms-h5p-player__empty rounded-lg border border-red-200 bg-red-50 p-8 text-center text-sm text-red-700">
        <p>{assetsError ?? runtimeError}</p>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="lms-h5p-player flex min-h-[12rem] items-center justify-center">
        <Loader label="Cargando actividad H5P…" />
      </div>
    );
  }

  const embedType = player.embedType ?? 'div';

  return (
    <div ref={wrapperRef} className="lms-h5p-player">
      {embedType === 'iframe' ? (
        <div className="lms-h5p-player__iframe-wrap h5p-iframe-wrapper">
          <iframe
            id={`h5p-iframe-${player.contentId}`}
            className="h5p-iframe w-full"
            data-content-id={player.contentId}
            style={{ height: '1px' }}
            src="about:blank"
            frameBorder={0}
            scrolling="no"
            title={player.title ?? 'H5P'}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
          />
        </div>
      ) : (
        <div className="lms-h5p-player__content h5p-content" data-content-id={player.contentId} />
      )}
    </div>
  );
}
